import { Badge } from "@/components/ui-lab/badge"
import { Button } from "@/components/ui-lab/button"
import { Card, CardContent } from "@/components/ui-lab/card"
import { Separator } from "@/components/ui-lab/separator"
import { FaGithub, FaLinkedin, FaWhatsapp } from "react-icons/fa"
import { MdEmail, MdPhone, MdLocationOn, MdAccessTime, MdSchedule } from "react-icons/md"
import { BsSave } from "react-icons/bs"
import { ContactForm } from "../ContactForm"
import { portfolioData } from "@/data/portfolioData"
import { siteConfig } from "@/config/siteConfig"
import { WHATSAPP_URL } from "../WhatsAppButton"

export const MyContactForm = () => {
  const { personal_info, availability } = portfolioData
  const phoneDisplay = personal_info.phone.replace(/^\+977/, "+977 ")
  const { address, city, country } = personal_info.location

  const contactDetails = [
    {
      icon: MdEmail,
      label: "Email",
      value: personal_info.email,
      href: `mailto:${personal_info.email}`,
    },
    {
      icon: MdPhone,
      label: "Phone",
      value: phoneDisplay,
      href: `tel:${personal_info.phone}`,
    },
    {
      icon: FaWhatsapp,
      label: "WhatsApp",
      value: phoneDisplay,
      href: WHATSAPP_URL,
      external: true,
    },
    {
      icon: MdLocationOn,
      label: "Location",
      value: `${address}, ${city}, ${country}`,
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${city}, ${country}`)}`,
      external: true,
    },
  ]

  const socialLinks = [
    { icon: FaGithub, href: personal_info.links.github, label: "GitHub" },
    { icon: FaLinkedin, href: personal_info.links.linkedin, label: "LinkedIn" },
  ]

  return (
    <>
      <h2 className="text-3xl font-semibold mb-6 flex items-center gap-2" suppressHydrationWarning>
        <MdEmail /> Get In Touch
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <Card className="lg:col-span-7 shadow-lg h-full hover:shadow-2xl transition-all duration-300">
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-4">Send Me a Message</h3>
            <p className="mb-4">
              Have a project in mind or want to discuss opportunities? Fill out the form below and I&apos;ll get back to
              you as soon as possible.
            </p>
            <ContactForm />
          </CardContent>
        </Card>
        <Card className="lg:col-span-5 h-full shadow-lg hover:shadow-2xl transition-all duration-300">
          <CardContent className="p-6 flex h-full flex-col">
            <h3 className="text-xl font-semibold mb-2" suppressHydrationWarning>
              Contact Information
            </h3>
            <p className="text-muted-foreground mb-6">Prefer a quick chat? Reach out directly through any of these.</p>
            <address className="not-italic flex flex-col gap-2">
              {contactDetails.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="group flex items-center gap-4 rounded-lg p-3 -mx-3 text-gray-900 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/30 focus-visible:bg-teal-50 dark:focus-visible:bg-teal-900/30 focus-visible:outline-none transition-colors duration-200"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400 transition-transform duration-200 group-hover:scale-110">
                    <item.icon className="text-xl" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">
                      {item.label}
                    </span>
                    <span className="block break-words font-semibold">{item.value}</span>
                  </span>
                </a>
              ))}
            </address>
            <Separator className="my-6" />
            <span className="block font-semibold mb-3">Connect With Me</span>
            <div className="flex gap-3">
              {socialLinks.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer me"
                  aria-label={item.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400 hover:bg-teal-700 hover:text-white dark:hover:bg-teal-500 dark:hover:text-white transition-colors duration-200"
                >
                  <item.icon className="text-xl" aria-hidden="true" />
                </a>
              ))}
            </div>
            <div className="mt-auto pt-6">
              <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-4 dark:border-teal-900/60 dark:bg-teal-900/20">
                {availability.open_to_work && (
                  <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                    </span>
                    Open to work
                  </span>
                )}
                <ul className="mb-3 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <MdSchedule className="shrink-0 text-base text-teal-700 dark:text-teal-400" aria-hidden="true" />
                    {availability.response_time}
                  </li>
                  <li className="flex items-center gap-2">
                    <MdAccessTime className="shrink-0 text-base text-teal-700 dark:text-teal-400" aria-hidden="true" />
                    {availability.time_zone}
                  </li>
                </ul>
                <div className="flex flex-wrap gap-2">
                  {availability.open_to.map((item) => (
                    <Badge key={item} variant="tech" className="font-normal">
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button size="lg" asChild className="mt-4 w-full">
                <a href={`/${siteConfig.user.resume.fileName}`} target="_blank" rel="noopener noreferrer">
                  <BsSave />
                  Download Resume
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
